import { FormEvent, useState } from 'react';
import { HoneypotInput } from 'src/components/atoms/HoneypotInput';
import { useContactForm } from 'src/hooks/useContactForm';
import { TURNSTILE_ACTIONS, useTurnstile } from 'src/hooks/useTurnstile';
import { useModal } from '../context/ModalContext';

export function ContactPanel() {
  const { submitContactForm, isLoading } = useContactForm();
  const { close, contact } = useModal();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [message, setMessage] = useState('');
  // Honeypot — humans never see this. Bots that auto-fill all inputs trip it.
  const [honeypot, setHoneypot] = useState('');

  const captcha = useTurnstile(TURNSTILE_ACTIONS.CONTACT);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!captcha.token) return;

    const result = await submitContactForm({
      name: name.trim(),
      email: email.trim(),
      company: company.trim(),
      message: message.trim(),
      captchaToken: captcha.token,
      honeypot,
    });

    if (result.success) {
      close();
      return;
    }
    // Turnstile tokens are single-use; refresh so the user can retry.
    captcha.reset();
  }

  const submitDisabled = isLoading || !captcha.token;

  return (
    <>
      <div className="mb-6">
        <h5>{contact.title}</h5>
        <p>Get in touch for live demo or any inquiries.</p>
      </div>
      <form className="contact-form" onSubmit={handleSubmit}>
        <div className="row gx-gs gy-5">
          <div className="col-md-6">
            <div className="form-group">
              <label className="form-label" htmlFor="contact-name">
                Name
              </label>
              <div className="form-control-wrap">
                <input
                  type="text"
                  id="contact-name"
                  required
                  maxLength={100}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="form-control form-control-xl"
                  placeholder="Enter your name"
                />
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label className="form-label" htmlFor="contact-company">
                Company
              </label>
              <div className="form-control-wrap">
                <input
                  type="text"
                  id="contact-company"
                  required
                  maxLength={200}
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  className="form-control form-control-xl"
                  placeholder="Enter your company"
                />
              </div>
            </div>
          </div>
          <div className="col-12">
            <div className="form-group">
              <label className="form-label" htmlFor="contact-email">
                Email
              </label>
              <div className="form-control-wrap">
                <input
                  type="email"
                  id="contact-email"
                  required
                  maxLength={255}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="form-control form-control-xl"
                  placeholder="Enter your email"
                />
              </div>
            </div>
          </div>
          <div className="col-12">
            <div className="form-group">
              <label className="form-label" htmlFor="contact-message">
                Message
              </label>
              <div className="form-control-wrap">
                <textarea
                  id="contact-message"
                  required
                  maxLength={1000}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="form-control form-control-xl"
                  rows={4}
                  placeholder="How can we help you drive progress today?"
                />
                <div className="fs-10 text-muted text-end mt-1">
                  {message.length} / 1000
                </div>
              </div>
            </div>
          </div>

          <HoneypotInput
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
          />

          <div className="col-12">
            <div ref={captcha.containerRef} />
          </div>

          <div className="col-12">
            <div className="pt-2">
              <button
                type="submit"
                disabled={submitDisabled}
                className="btn btn-primary btn-lg btn-pill">
                {isLoading
                  ? 'Sending…'
                  : !captcha.ready
                    ? 'Loading verification…'
                    : !captcha.token
                      ? 'Complete verification'
                      : contact.submitText}
              </button>
            </div>
          </div>
        </div>
      </form>
    </>
  );
}
